import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import {
  CohortStatusLegacy,
  FilterTypeLegacy,
  SimpleEventTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { useCanAddFilters } from './useCanAddFilters';
import { useCanSaveCohortItem } from './useCanSaveCohortItem';
import { useCohortBuilderItemIsInUse } from './useCohortBuilderItemIsInUse';
import { useCohortEditable } from './useCohortEditable';
import { useConceptFilters } from './useConceptFilters';
import { useNumericFilters } from './useNumericFilters';
import { useRelatedEntityFilters } from './useRelatedEntityFilters';
import { useSimpleEventScroll } from './useSimpleEventScroll';
import { useUniquenessFilters } from './useUniquenessFilters';
import {
  clearModal,
  setActiveCohortBuilderItemFilterInputFieldValue,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemSimpleEventType,
  setSimpleEvent,
} from '../cohortBuilderSlice';
import { NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE } from '../constants/constants';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemName,
  selectActiveCohortBuilderItemSimpleEventType,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDefinition,
  selectDerivedEvents,
  selectSimpleEvents,
  selectSubjectVariables,
} from '../selectors/cohortDefinitionSelectors';
import { validateName } from '../utils/checkNameUniqueness';
import { getFullCohortDslForSimpleEvent } from '../utils/dsl-generators/getFullCohortDslForSimpleEvent';

export const useSimpleEventEdit = () => {
  const dispatch = useAppDispatch();
  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;

  /** Cohort Builder Workflow - Edit Modal State */
  const simpleEventId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new simple event

  // Summary State
  const name = useAppSelector(selectActiveCohortBuilderItemName) ?? '';
  const type = useAppSelector(selectActiveCohortBuilderItemSimpleEventType);

  const modal = useAppSelector(selectModal);
  const isSimpleEventIsInUse = useCohortBuilderItemIsInUse(simpleEventId);

  // Filter State
  const { conceptFilters, onAddConceptFilterClick, onDeleteConceptFilterClick } =
    useConceptFilters();
  const { numericFilters, onAddNumericFilterClick, onDeleteNumericFilterClick } =
    useNumericFilters();
  const { relatedEntityFilters, onAddRelatedEntityFilterClick, onDeleteRelatedEntityFilterClick } =
    useRelatedEntityFilters();
  const { uniquenessFilters, onAddUniquenessFilterClick, onDeleteUniquenessFilterClick } =
    useUniquenessFilters();

  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};

  const { isSaveAllowed } = useCanSaveCohortItem<SimpleEventTypeLegacy>(name, type, [
    ...conceptFilters,
    ...numericFilters,
    ...uniquenessFilters,
    ...relatedEntityFilters,
  ]);

  const {
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,
    uniquenessFilterRef,
    relatedEntityFilterRef,
  } = useSimpleEventScroll(conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters);

  const { enableAddFilterButtons, addFilterButtonTitleText, typeInputFieldTitleText } =
    useCanAddFilters<SimpleEventTypeLegacy>(name, type, isSimpleEventIsInUse);

  const currentCohortDefinition = useAppSelector(selectCohortDefinition);

  /** user to check for duplicate names */
  const subjectVariables = useAppSelector(selectSubjectVariables);
  const derivedEvents = useAppSelector(selectDerivedEvents);
  const simpleEvents = useAppSelector(selectSimpleEvents);

  const [nameInError, setNameInError] = useState<boolean>(false);
  const [nameErrorMsg, setNameErrorMsg] = useState<string>('');

  const onNameBlur = (id: string, value: string, inError: boolean, trimValue: string) => {
    const { errorMsg, hasError } = validateName(
      simpleEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  };

  // check on modal/card open (on mount) using useEffect:
  useEffect(() => {
    const { errorMsg, hasError } = validateName(
      simpleEventId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  }, [name, derivedEvents, simpleEvents, subjectVariables, simpleEventId]);

  const onNameChange = (_id: string, value: string) => {
    !viewMode &&
      dispatch(
        setActiveCohortBuilderItemName({
          id: simpleEventId,
          name: value,
        })
      );

    setNameErrorMsg('');
    setNameInError(false);
  };

  const onTypeChange = (id: string, value: string) => {
    !isSimpleEventIsInUse &&
      !viewMode &&
      value &&
      dispatch(
        setActiveCohortBuilderItemSimpleEventType({
          id: simpleEventId,
          type: value as SimpleEventTypeLegacy,
        })
      );
  };

  const onFilterInputValueChange = (
    filterId: string,
    inputFieldId: string,
    value: string,
    filterType: FilterTypeLegacy
  ) => {
    !viewMode &&
      dispatch(
        setActiveCohortBuilderItemFilterInputFieldValue({
          filterType,
          filterId,
          inputFieldId,
          value,
        })
      );
  };

  /** Cohort Builder Edit Modal Action Handlers */
  const onSaveSimpleEvent = (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => {
    const _nextSimpleEvent = {
      id: simpleEventId,
      name: name.trim(),
      type: type,
      conceptFilters,
      numericFilters,
      relatedEntityFilters,
      uniquenessFilters,
    };

    if (!viewMode) {
      dispatch(setSimpleEvent(_nextSimpleEvent));

      const nextCohortDsl = getFullCohortDslForSimpleEvent(
        currentCohortDefinition,
        _nextSimpleEvent
      );

      emitResearchAssistantUpdate(nextCohortDsl);
    }
  };

  const onCancelSimpleEvent = () => {
    dispatch(clearModal());
  };

  return {
    /** Cohort Builder Edit Modal State */
    name,
    type,
    modal,
    enableAddFilterButtons,
    conceptFilters,
    numericFilters,
    relatedEntityFilters,
    uniquenessFilters,
    isSaveAllowed,
    inputFieldConfigMap,
    isSimpleEventIsInUse,

    addFilterButtonTitleText,
    typeInputFieldTitleText,

    /** Cohort Builder Edit Modal Internal State Side Effects */
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters: NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE,
    onTypeChange,
    onFilterInputValueChange,
    onAddConceptFilterClick,
    onDeleteConceptFilterClick,
    onAddNumericFilterClick,
    onDeleteNumericFilterClick,
    onAddRelatedEntityFilterClick,
    onDeleteRelatedEntityFilterClick,
    onAddUniquenessFilterClick,
    onDeleteUniquenessFilterClick,

    /** Cohort Builder Edit Modal Action Handlers */
    onSaveSimpleEvent,
    onCancelSimpleEvent,

    /** Auto Scroll when adding filters **/
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,
    relatedEntityFilterRef,
    uniquenessFilterRef,
    isEditable,
    viewMode,
  };
};
